import React, { useState, useEffect } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Settings, Plug, Shield, Zap, UserCheck, Apple, Monitor, ChevronDown, ChevronRight, ChevronLeft, Grid2X2, Chrome, Egg, Bot, Mail, KeyRound, Sparkles } from "lucide-react";
import { patchPolicySchema, PatchPolicyFormValues } from "@/components/patching/schema";
import { Form } from "@/components/ui/form";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useCompactMode } from "@/hooks/useCompactMode";
import { useIsMobile } from "@/hooks/use-mobile";

// Import category components
import { SystemIntegrationSettings } from "@/components/settings/SystemIntegrationSettings";
import { IdentityProvidersSettings } from "@/components/settings/IdentityProvidersSettings";
import { GeneralSettings } from "@/components/settings/GeneralSettings";
import { AppleSettings } from "@/components/settings/AppleSettings";
import { EmailTemplatesSettings } from "@/components/settings/EmailTemplatesSettings";

type SettingsCategory = {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
};

// General settings tabs
// Note: API Keys, Theme Generator, Security, and Advanced are temporarily
// hidden until those areas are implemented.
const generalCategories: SettingsCategory[] = [
  { id: "general", label: "General", icon: Settings },
  { id: "system", label: "Integrations", icon: Plug },
  { id: "identity", label: "Identity Providers", icon: UserCheck },
  { id: "emails", label: "Email Templates", icon: Mail },
  // { id: "api-keys", label: "API Keys", icon: KeyRound },
  // { id: "theme-generator", label: "Theme Generator", icon: Sparkles },
  // { id: "security", label: "Security", icon: Shield },
  // { id: "advanced", label: "Advanced", icon: Zap },
];

// Platform-specific settings
// Non-Apple platforms hidden until implemented.
const platformCategories: SettingsCategory[] = [
  { id: "apple", label: "Apple", icon: Apple },
  // { id: "android", label: "Android", icon: Bot },
  // { id: "chromeos", label: "ChromeOS", icon: Chrome },
  // { id: "linux", label: "Linux", icon: Egg },
  // { id: "windows", label: "Windows", icon: Grid2X2 },
];

const allCategories = [...generalCategories, ...platformCategories];

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const categoryFromUrl = searchParams.get("category");
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl || "general");
  const { cx } = useCompactMode();

  // Update selected category when URL parameter changes
  useEffect(() => {
    if (categoryFromUrl && allCategories.find(cat => cat.id === categoryFromUrl)) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [categoryFromUrl]);

  const form = useForm<PatchPolicyFormValues>({
    resolver: zodResolver(patchPolicySchema),
    defaultValues: {
      name: "",
      description: "",
      schedule_type: "manual",
      restart_required: false,
      defer_max_times: 3,
      defer_duration_minutes: 60,
      enabled: true,
      update_method: "default",
      force_install_days: 90,
      allowed_days: [],
      show_notifications: true,
      notifications_timing: "all",
      allow_major_updates: true,
      allow_minor_updates: true,
      allow_security_updates: true,
      auto_download: true,
      auto_install_os_updates: false,
      auto_install_security_updates: true,
      enable_rsr_installation: false,
      enable_rsr_rollback: false,
      smtp_host: "",
      smtp_port: 587,
      smtp_username: "",
      smtp_password: "",
      smtp_from_address: "",
      apns_certificate: "",
      apns_certificate_password: "",
      abm_token: "",
      abm_organization_id: "",
      abm_server_token: ""
    }
  });

  const handleCategoryChange = (value: string) => {
    // "API Keys" and "Theme Generator" each live on their own route so the
    // forms have room to breathe and direct-link / refresh works cleanly.
    // Navigate instead of swapping the inline tab content.
    if (value === "api-keys") {
      navigate("/settings/api-keys");
      return;
    }
    if (value === "theme-generator") {
      navigate("/settings/theme-generator");
      return;
    }
    setSelectedCategory(value);
    setSearchParams({ category: value });
  };

  // Check if current selection is a platform
  const selectedPlatform = platformCategories.find(p => p.id === selectedCategory);
  const isPlatformSelected = !!selectedPlatform;

  const renderCategoryContent = () => {
    switch (selectedCategory) {
      case "general":
        return <GeneralSettings form={form} />;
      case "system":
        return <SystemIntegrationSettings form={form} />;
      case "apple":
        return <AppleSettings form={form} />;
      case "identity":
        return <IdentityProvidersSettings />;
      case "emails":
        return <EmailTemplatesSettings />;
      case "security":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Security Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Security configurations will be available in a future update. Configure access controls, audit logs, and security policies.
            </p>
          </div>
        );
      case "advanced":
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Zap className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Advanced Settings</h3>
            <p className="text-muted-foreground max-w-md">
              Advanced configurations and integrations will be available in a future update. Manage custom workflows and API integrations.
            </p>
          </div>
        );
      case "android":
      case "chromeos":
      case "linux":
      case "windows":
        const platform = platformCategories.find(p => p.id === selectedCategory);
        const PlatformIcon = platform?.icon || Monitor;
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <PlatformIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">{platform?.label} Settings</h3>
            <p className="text-muted-foreground max-w-md">
              {platform?.label} platform configurations will be available in a future update.
            </p>
          </div>
        );
      default:
        return null;
    }
  };

  const isMobile = useIsMobile();

  // Mobile: categories list → push-detail
  if (isMobile) {
    const activeCat = allCategories.find((c) => c.id === selectedCategory);
    const inDetail = !!categoryFromUrl && !!activeCat;

    if (inDetail && activeCat) {
      const Icon = activeCat.icon;
      return (
        <div className="min-h-screen bg-background w-full">
          <div className="sticky top-0 z-10 flex items-center gap-2 border-b bg-background/95 backdrop-blur px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="min-h-[44px] gap-1 -ml-2"
              onClick={() => {
                setSearchParams({});
                setSelectedCategory("general");
              }}
            >
              <ChevronLeft className="w-4 h-4" />
              Settings
            </Button>
            <div className="ml-auto flex items-center gap-2 text-sm font-semibold">
              <Icon className="w-4 h-4 text-muted-foreground" />
              <span>{activeCat.label}</span>
            </div>
          </div>
          <div className="px-3 py-4">
            <Form {...form}>
              <form className="space-y-4">{renderCategoryContent()}</form>
            </Form>
          </div>
        </div>
      );
    }

    const Group = ({ title, items }: { title: string; items: SettingsCategory[] }) => (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-1">
          {title}
        </h3>
        <div className="rounded-lg border bg-card divide-y">
          {items.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left hover:bg-muted/50 active:bg-muted transition-colors"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
                  <Icon className="w-4 h-4 text-foreground" />
                </span>
                <span className="flex-1 text-sm font-medium">{cat.label}</span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className="min-h-screen bg-background w-full">
        <div className="px-3 py-4 space-y-5">
          <PageHeader title="Settings" description="Manage your application settings and integrations" />
          <Group title="General" items={generalCategories} />
          <Group title="Platforms" items={platformCategories} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background w-full">
      <div className={cx("w-full space-y-6 px-6 py-6", "w-full space-y-4 px-6 py-4")}>
        <PageHeader title="Settings" description="Manage your application settings and integrations" />

        {/* Tabs with Platforms Dropdown */}
        <Tabs value={isPlatformSelected ? "" : selectedCategory} onValueChange={handleCategoryChange} className={cx("space-y-6", "space-y-4")}>
          <div className="flex items-center">
            <TabsList>
              {generalCategories.map((category) => (
                <TabsTrigger
                  key={category.id}
                  value={category.id}
                  className="hover:bg-transparent hover:text-current"
                >
                  <category.icon className="w-4 h-4 mr-2" />
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Platforms Dropdown */}
            <div className={cn(
              "inline-flex h-9 items-center justify-center rounded-lg p-1 ml-2 w-[140px] transition-colors",
              selectedCategory === "apple" && "bg-muted border border-border shadow-sm",
              selectedCategory === "android" && "bg-success border border-success shadow-sm",
              selectedCategory === "chromeos" && "bg-destructive border border-destructive shadow-sm",
              selectedCategory === "linux" && "bg-zinc-900 border border-zinc-900 shadow-sm",
              selectedCategory === "windows" && "bg-info border border-info shadow-sm"
            )}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 w-full",
                      isPlatformSelected 
                        ? "bg-background text-muted-foreground shadow" 
                        : "bg-muted text-muted-foreground border border-border shadow-sm"
                    )}
                  >
                    {selectedPlatform ? (
                      <>
                        <selectedPlatform.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                        <span className="truncate">{selectedPlatform.label}</span>
                      </>
                    ) : (
                      <span>Platforms</span>
                    )}
                    <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="z-50 bg-popover">
                  {platformCategories.map((platform) => (
                    <DropdownMenuItem
                      key={platform.id}
                      onClick={() => handleCategoryChange(platform.id)}
                      className={cn(
                        "cursor-pointer",
                        selectedCategory === platform.id && "bg-accent"
                      )}
                    >
                      <platform.icon className="w-4 h-4 mr-2" />
                      {platform.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <TabsContent value={selectedCategory} className="mt-0" forceMount={isPlatformSelected ? true : undefined}>
            <Form {...form}>
              <form className={cx("space-y-6", "space-y-4")}>
                {renderCategoryContent()}
              </form>
            </Form>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}